import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import React, { useState } from 'react';
import { format } from 'date-fns';
import alphanumSort from 'helpers/sorters';
import { getHeaderSortClassName } from 'helpers/tableSort';
import { Applicator } from '../../hooks/useDetailsTabData';

import './style.css';

const ApplicatorsTable = ({ applicators }: { applicators: Applicator[] }) => {
  const [sortInfo, setSortInfo] = useState<{
    columnKey: string;
    order: 'ascend' | 'descend' | null;
  }>({ columnKey: '', order: null });

  const handleTableChange = (_: any, __: any, sorter: any) => {
    setSortInfo({
      columnKey: sorter.columnKey,
      order: sorter.order
    });
  };

  const handleHeaderSortClassName = (columnKey: string) => {
    return getHeaderSortClassName(
      columnKey,
      sortInfo.columnKey,
      sortInfo.order
    );
  };

  const handleSortOrder = (columnKey: string) => {
    return sortInfo.columnKey === columnKey ? sortInfo.order : null;
  };

  const columns = [
    {
      title: 'Applicator Name',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      sorter: (a: Applicator, b: Applicator) => alphanumSort([a, b], 'name'),
      className: handleHeaderSortClassName('name'),
      sortOrder: handleSortOrder('name'),
      showSorterTooltip: false,
      render: (_: any, record: Applicator) => (
        <span
          className="line-clamp-2"
          data-testid="applicator-name"
          title={record.name ?? '---'}
        >
          {record.name ?? '---'}
        </span>
      )
    },
    {
      title: 'License',
      dataIndex: 'licenseNumber',
      key: 'licenseNumber',
      align: 'left' as const,
      render: (_: any, record: Applicator) => {
        return (
          <div data-testid="license-number-container">
            <p
              data-testid={
                record.licenseNumber ? `license-number` : `no-license-number`
              }
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(record.licenseNumber && {
                title: record.licenseNumber,
                className: 'truncate'
              })}
            >
              {record.licenseNumber ? record.licenseNumber : '---'}
            </p>

            <p
              className="text-xs text-neutral-60"
              data-testid={
                record.expirationDate ? 'expiration-date' : 'no-expiration-date'
              }
            >
              {record.expirationDate
                ? `Expires ${format(
                    new Date(record.expirationDate),
                    'MMM d, yyyy'
                  )}`
                : '---'}
            </p>
          </div>
        );
      }
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      align: 'left' as const,
      sorter: (a: Applicator, b: Applicator) => alphanumSort([a, b], 'company'),
      className: handleHeaderSortClassName('company'),
      sortOrder: handleSortOrder('company'),
      showSorterTooltip: false,
      render: (_: any, record: Applicator) => (
        <span
          className="line-clamp-2"
          data-testid="company-name"
          title={record.company ?? '---'}
        >
          {record.company ?? '---'}
        </span>
      )
    }
  ];

  const getSortedData = () => {
    if (!sortInfo.order || !sortInfo.columnKey) return applicators;

    const sortableColumns = ['name', 'company'];

    if (!sortableColumns.includes(sortInfo.columnKey)) {
      return applicators;
    }

    return alphanumSort(
      [...applicators],
      sortInfo.columnKey,
      sortInfo.order === 'ascend' ? 'asc' : 'desc'
    );
  };

  return (
    <div data-testid="applicators-table" className="applicators-table">
      <Table
        columns={columns}
        dataSource={getSortedData()}
        pagination={false}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ApplicatorsTable;
